'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, HStack, Text, VStack } from '@jaaq/hub-ui';
import { cn } from '@jaaq/hub-ui/cn';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import type { ReactElement } from 'react';
import { useOnboardingState } from '#lib/onboarding-provider';
import { getWizardProgress } from '#lib/steps/get-wizard-progress';
import { type OnboardingStep, STEP_ORDER } from '#lib/steps/step';
import { JaaqLogo } from './jaaq-logo';
import { ProgressBar } from './progress-bar';
type OnboardingShellProps = {
  step?: OnboardingStep;
  showProgress?: boolean;
  showActions?: boolean;
  canContinue?: boolean;
  onContinue?: () => void;
  onBack?: () => void;
  showHelp?: boolean;
  /** Centre the content column on the page and centre-align its content. */
  centred?: boolean;
  /** Max-width of the content column. Defaults to 600px. */
  contentMaxWidth?: string;
  /** Override the progress indicator — use for sub-flows that have their own 1/3, 2/3, 3/3. */
  customProgress?: {
    index: number;
    total: number;
  };
  children: React.ReactNode;
};
export const OnboardingShell = t0 => {
  const $ = _c(35);
  const {
    step,
    showProgress: t1,
    showActions: t2,
    canContinue: t3,
    onContinue,
    onBack,
    showHelp: t4,
    centred: t5,
    contentMaxWidth,
    customProgress,
    children
  } = t0;
  const showProgress = t1 === undefined ? true : t1;
  const showActions = t2 === undefined ? true : t2;
  const canContinue = t3 === undefined ? true : t3;
  const showHelp = t4 === undefined ? true : t4;
  const centred = t5 === undefined ? false : t5;
  const {
    onboardingState,
    goBack,
    goNext
  } = useOnboardingState();
  const activeStep = step ?? onboardingState.currentStep;
  let t6;
  if ($[0] !== activeStep || $[1] !== customProgress || $[2] !== showProgress) {
    t6 = customProgress ?? (showProgress ? getWizardProgress(activeStep) : null);
    $[0] = activeStep;
    $[1] = customProgress;
    $[2] = showProgress;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const progress = t6;
  const stepIdx = STEP_ORDER.indexOf(activeStep);
  const showBack = stepIdx > STEP_ORDER.indexOf("outcome");
  const maxWidth = contentMaxWidth ?? "600px";
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <JaaqLogo />;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const t8 = centred && "mx-auto";
  let t9;
  if ($[5] !== t8) {
    t9 = cn("w-full", t8);
    $[5] = t8;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  let t10;
  if ($[7] !== maxWidth) {
    t10 = {
      maxWidth
    };
    $[7] = maxWidth;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] !== progress) {
    t11 = progress && <ProgressBar current={progress.index} total={progress.total} align="left" />;
    $[9] = progress;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const t12 = centred && "text-center";
  let t13;
  if ($[11] !== t12) {
    t13 = cn(t12);
    $[11] = t12;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] !== canContinue || $[14] !== goBack || $[15] !== goNext || $[16] !== onBack || $[17] !== onContinue || $[18] !== showActions || $[19] !== showBack) {
    t14 = showActions && <HStack align="center" gap={5}>{showBack && <Button variant="outlined" onClick={onBack ?? goBack}><ArrowLeft className="size-4" strokeWidth={1.5} />Back</Button>}<Button variant="filled" disabled={!canContinue} onClick={onContinue ?? goNext}>Continue<ArrowRight className="size-4" strokeWidth={1.5} /></Button></HStack>;
    $[13] = canContinue;
    $[14] = goBack;
    $[15] = goNext;
    $[16] = onBack;
    $[17] = onContinue;
    $[18] = showActions;
    $[19] = showBack;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== children || $[22] !== t13 || $[23] !== t14) {
    t15 = <VStack gap={8} className={t13}>{children}{t14}</VStack>;
    $[21] = children;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t15 || $[28] !== t9) {
    t16 = <VStack gap={10} className={t9} style={t10}>{t11}{t15}</VStack>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t15;
    $[28] = t9;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== showHelp) {
    t17 = showHelp ? <Text tone="inverse" muted={true} className="text-right">Having trouble?{" "}<a href="https://jaaq.app/book-a-demo" target="_blank" rel="noreferrer" className="font-bold underline underline-offset-4">Get help</a></Text> : <span />;
    $[30] = showHelp;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t16 || $[33] !== t17) {
    t18 = <VStack justify="between" className="jaaq-hero-bg min-h-screen text-fg-inverse p-10">{t7}{t16}{t17}</VStack>;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  return t18;
};